import { CloseOutlined } from '@ant-design/icons';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PhoneInput } from '../../../../common/components/PhoneInput/PhoneInput';
import {
  EMAIL_MATCH_REGEX,
  PHONE_MATCH_REGEX,
} from '../../../../common/constants/validations';
import { EmailInput } from '../../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import { useSetNextOnboardingStep } from '../../SignupOnboarding/use-set-next-onboarding-step';
import { ReactComponent as Stars } from '../WebsiteReportSummary/Icons/stars.svg';
import styles from './ContactSupport.module.css';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';

export const ContactSupport = ({
  type,
  setShowContactSupport,
  promoter,
  onClick,
}: {
  type: 'email' | 'phone' | 'thankYou';
  setShowContactSupport: React.Dispatch<
    React.SetStateAction<'email' | 'phone' | 'thankYou' | undefined>
  >;
  promoter: PromoterModel;
  onClick?: () => void;
}) => {
  const facebookPage = useSelector(
    (state: StoreState) =>
      state.connectedPage.entities[0] || activeFacebookPageSelector(state),
  );
  const [inputValue, setInputValue] = useState('');
  const { setNextOnboardingStep } = useSetNextOnboardingStep();
  const { update } = usePromoterCRUDActions();

  const isAutomations = window?.location?.pathname?.includes('automations');
  const isIntegrations = window?.location?.pathname?.includes('integrations');

  const updatePromoter = async (phoneOrEmail: string) => {
    if (promoter) {
      await update(facebookPage.activeBotId, {
        ...promoter,
        scheduleGuided: phoneOrEmail,
      });
      setShowContactSupport('thankYou');
    }
    setShowContactSupport('thankYou');
  };

  const config = {
    email: {
      header: 'Schedule a guided installation',
      subheader: null,
      buttonName: 'Send',
      withCancel: true,
      withInput: true,
      onClick: updatePromoter,
      icon: null,
      input: (
        <EmailInput
          inputProps={{
            placeholder: '',
            style: { border: '1px solid #D9D9D9', minHeight: '40px' },
          }}
          onChange={p => setInputValue(p.email)}
          className={styles.emailInput}
          withIcon={false}
          strict
        />
      ),
      submitDsabled: !inputValue || !EMAIL_MATCH_REGEX.test(inputValue),
    },
    phone: {
      header: 'Call me now',
      subheader:
        'Please leave your phone number to help you to install generator script',
      buttonName: 'Send',
      withCancel: true,
      withInput: true,
      onClick: updatePromoter,
      icon: null,
      input: (
        <PhoneInput
          value={inputValue}
          onChange={val => setInputValue(val)}
          className={styles.input}
        />
      ),
      submitDsabled: !inputValue || !PHONE_MATCH_REGEX.test(inputValue),
    },
    thankYou: {
      header: 'Thank you!',
      subheader: 'A member of our team will reach out shortly.',
      buttonName: 'Go to Dashboard',
      withCancel: false,
      withInput: false,
      icon: <Stars className={styles.stars} />,
      input: <></>,
      onClick: () => {
        setNextOnboardingStep(OnboardingSignupStep.NONE);
      },
      submitDsabled: false,
    },
  };

  return (
    <div className={styles.container}>
      <CloseOutlined
        className={styles.closeIcon}
        onClick={() => setShowContactSupport(undefined)}
      />

      {config[type]?.icon}
      <div className={styles.title}>
        <div className={styles.heading}>{config[type].header} </div>
        <div className={styles.subHeading}>{config[type]?.subheader}</div>
      </div>

      {config[type].withInput && (
        <div className={styles.form}>
          {type === 'email' ? 'Email' : 'Phone number'}
          {config[type].input}
        </div>
      )}
      <div
        className={classNames(styles.buttons, {
          [styles.centered]: type === 'thankYou',
        })}
      >
        {config[type].withCancel && (
          <PrimaryButton
            className={styles.cancelButton}
            onClick={() => setShowContactSupport(undefined)}
          >
            Cancel
          </PrimaryButton>
        )}

        {(isAutomations || isIntegrations) && type === 'thankYou' ? (
          <></>
        ) : (
          <PrimaryButton
            className={styles.button}
            onClick={() => {
              config[type]?.onClick(inputValue);
              onClick?.();
            }}
            disabled={config[type].submitDsabled}
          >
            {config[type].buttonName}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};
